// @ts-check
import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import Card from "../../../../components/atoms/Card.atom"
import {
  ValueWithLabelContainer,
  ValueWithLabelTemplate,
} from "../../../../components/layouts/containers/ValueWithLabelElements.container"
import ModalForm from "../../../../components/compounds/modalForm/ModalForm.compunds"
import IconWithTooltip from "../../../../components/molecules/IconWithTooltip.molecule"
import { getMonthLongNameByNumber } from "../../../../utils/date.utils"
import VacationRequestCard from "../../components/VacationRequestCard.compound"
import { getAdminVacationResumeByMonth } from "../../../../services/vacation.service"
import ModalFormApprovedRequestReview from "./ModalFormApprovedRequestReview.controller"
import MonthNavigator from "../../components/MonthNavigator.compound"
import { VACATION_REQUEST_PLUS_DAYS } from "../../../../consts/modules/vacations.consts"

const AdminVacationsResume = ({
  totalsSummary,
  loading,
  selectedMonth,
  triggerRefresh,
}) => {
  const [
    isProgrammedPaymentsModalVisible,
    setIsProgrammedPaymentsModalVisible,
  ] = React.useState(false)
  const [isOverduePaymentsModalVisible, setIsOverduePaymentsModalVisible] =
    React.useState(false)

  const [modalMonth, setModalMonth] = useState(null)
  const [baseMonth, setBaseMonth] = useState(null)

  useEffect(() => {
    if (selectedMonth) {
      setModalMonth(selectedMonth)
      setBaseMonth(selectedMonth)
    }
  }, [selectedMonth, baseMonth])

  const programmedPaymentsModalToggle = async () => {
    const opening = !isProgrammedPaymentsModalVisible
    setIsProgrammedPaymentsModalVisible(opening)

    if (opening && baseMonth) {
      setModalMonth(baseMonth)

      await fetchVacationRequests(
        selectedMonth.year,
        selectedMonth.month,
        VACATION_REQUEST_PLUS_DAYS.CURRENT
      )
    }
  }
  const [visibleRequestModal, setVisibleRequestModal] = useState(false)

  const overduePaymentsModalToggle = async () => {
    const opening = !isOverduePaymentsModalVisible
    setIsOverduePaymentsModalVisible(opening)

    if (opening && baseMonth) {
      setModalMonth(baseMonth)

      await fetchVacationRequests(
        selectedMonth.year,
        selectedMonth.month,
        VACATION_REQUEST_PLUS_DAYS.OVERDUE
      )
    }
  }

  const goNextMonth = () => {
    const date = new Date(modalMonth.year, modalMonth.month - 1)
    date.setMonth(date.getMonth() + 1)

    if (date > maxDate) return

    setModalMonth({
      year: date.getFullYear(),
      month: date.getMonth() + 1,
    })
  }

  const goPrevMonth = () => {
    const date = new Date(modalMonth.year, modalMonth.month - 1)
    date.setMonth(date.getMonth() - 1)

    if (date < minDate) return

    setModalMonth({
      year: date.getFullYear(),
      month: date.getMonth() + 1,
    })
  }

  const minDate =
    baseMonth ? new Date(baseMonth.year, baseMonth.month - 1) : null

  const maxDate =
    baseMonth ? new Date(baseMonth.year, baseMonth.month - 1) : null

  if (minDate) minDate.setMonth(minDate.getMonth() - 6)
  if (maxDate) maxDate.setMonth(maxDate.getMonth() + 12)

  const currentDate =
    modalMonth ? new Date(modalMonth.year, modalMonth.month - 1) : null

  const canGoPrev = currentDate && minDate ? currentDate > minDate : false
  const canGoNext = currentDate && maxDate ? currentDate < maxDate : false

  const monthName = getMonthLongNameByNumber(selectedMonth?.month)
  const [vacationRequests, setVacationRequests] = useState([])
  const [selectedVacationRequest, setSelectedVacationRequest] =
    React.useState(null)
  const [isLoadingRequests, setIsLoadingRequests] = useState(false)

  const fetchVacationRequests = async (year, month, type) => {
    setIsLoadingRequests(true)

    try {
      const response = await getAdminVacationResumeByMonth(year, month, type)

      if (!response.success) throw new Error(response.message)

      setVacationRequests(response.result)
    } catch (error) {
      console.error("Error fetching vacation requests:", error)
    } finally {
      setIsLoadingRequests(false)
    }
  }

  useEffect(() => {
    if (!isProgrammedPaymentsModalVisible) return

    fetchVacationRequests(
      modalMonth.year,
      modalMonth.month,
      VACATION_REQUEST_PLUS_DAYS.CURRENT
    )
  }, [modalMonth])

  const handleTriggerRefresh = async () => {
    if (!modalMonth) return

    const type =
      isProgrammedPaymentsModalVisible ?
        VACATION_REQUEST_PLUS_DAYS.CURRENT
      : VACATION_REQUEST_PLUS_DAYS.OVERDUE

    await fetchVacationRequests(modalMonth.year, modalMonth.month, type)

    triggerRefresh()
  }

  const emptyResultsTemplate = () => {
    return (
      <div className="empty-data-vacation-requests text-center flex flex-column justify-content-center w-full">
        <i className="pi pi-folder-open"></i>

        <h3 className="title font-cairo">No tienes pagos pendientes</h3>

        <p className="message">
          Aquí aparecerán los pagos que tengas por programar.
        </p>
      </div>
    )
  }

  const renderVacationRequests = () => {
    if (isLoadingRequests) {
      return <p>Cargando...</p>
    }

    if (!vacationRequests || vacationRequests.length === 0) {
      return emptyResultsTemplate()
    }

    return vacationRequests.map((vacationRequest, index) => {
      const teamMember = vacationRequest.teamMember

      return (
        <VacationRequestCard
          key={vacationRequest.id || index}
          vacationRequest={vacationRequest}
          teamMember={teamMember}
          onClick={() => {
            setSelectedVacationRequest(vacationRequest)
            setOriginModal(VACATION_REQUEST_PLUS_DAYS.PROGRAMMED)
            setIsProgrammedPaymentsModalVisible(false)
            setIsOverduePaymentsModalVisible(false)
            setVisibleRequestModal(true)
          }}
        />
      )
    })
  }
  const handleRequestModalVisibility = (visible) => {
    setVisibleRequestModal(visible)

    if (!visible) {
      if (originModal === VACATION_REQUEST_PLUS_DAYS.OVERDUE) {
        if (vacationRequests?.length > 0) {
          setIsOverduePaymentsModalVisible(true)
        }
      } else if (originModal === VACATION_REQUEST_PLUS_DAYS.PROGRAMMED) {
        setIsProgrammedPaymentsModalVisible(true)
      }
    }
  }
  const [originModal, setOriginModal] = useState(null)

  useEffect(() => {
    if (
      isOverduePaymentsModalVisible &&
      !isLoadingRequests &&
      vacationRequests?.length === 0
    ) {
      setIsOverduePaymentsModalVisible(false)
    }
  }, [vacationRequests, isLoadingRequests, isOverduePaymentsModalVisible])

  return (
    <>
      <Card className="shadow-2 admin-vacations-resume-card">
        <ValueWithLabelContainer>
          <ValueWithLabelTemplate
            label="Solicitudes pendientes"
            value={totalsSummary.pendingRequests}
            loading={loading}
            showZeroValue
          />
          <ValueWithLabelTemplate
            label="Solicitudes de cambio"
            value={totalsSummary.changeRequests}
            loading={loading}
            showZeroValue
          />

          <ValueWithLabelTemplate
            label="Pagos a programar"
            value={
              <div className="flex align-items-center gap-2">
                <span>{totalsSummary.daysPlusToPayRequests}</span>

                <IconWithTooltip
                  tooltipValue={`Clic para ver pagos a programar en ${monthName}`}
                  position="right"
                  iconClassName="pi pi-calendar icon-calendar-daysplus cursor-pointer"
                  onClick={programmedPaymentsModalToggle}
                />
              </div>
            }
            loading={loading}
            showZeroValue
          />

          {totalsSummary.overdueDaysPlusRequests > 0 && (
            <ValueWithLabelTemplate
              label="Pagos rezagados"
              value={
                <div className="flex align-items-center gap-2">
                  <span>{totalsSummary.overdueDaysPlusRequests}</span>

                  <IconWithTooltip
                    tooltipValue="Clic para ver los pagos rezagados"
                    position="right"
                    iconClassName="pi pi-exclamation-triangle text-red-500 cursor-pointer"
                    onClick={overduePaymentsModalToggle}
                  />
                </div>
              }
              loading={loading}
            />
          )}
        </ValueWithLabelContainer>
      </Card>
      <ModalForm
        visible={isProgrammedPaymentsModalVisible}
        setVisible={setIsProgrammedPaymentsModalVisible}
        headerTitle="Pagos a programar"
        closable
        withoutFooter
        headerIconClass="pi pi-calendar"
        className="payments-modal"
      >
        <MonthNavigator
          month={modalMonth?.month}
          year={modalMonth?.year}
          onPrev={goPrevMonth}
          onNext={goNextMonth}
          disablePrev={!canGoPrev}
          disableNext={!canGoNext}
        />

        <div className="vacation-scroll-area">
          <div
            className={`vacation-cards-container ${!vacationRequests?.length ? "empty" : ""}`}
          >
            {renderVacationRequests()}
          </div>
        </div>
      </ModalForm>

      <ModalForm
        visible={isOverduePaymentsModalVisible}
        setVisible={setIsOverduePaymentsModalVisible}
        headerTitle="Pagos rezagados"
        closable
        withoutFooter
        headerIconClass="pi pi-calendar"
        className="payments-modal"
      >
        <div className="vacation-scroll-area">
          <div className="vacation-cards-container">
            {isLoadingRequests ?
              <p>Cargando...</p>
            : vacationRequests.map((vacationRequest, index) => {
                const teamMember = vacationRequest.teamMember

                return (
                  <VacationRequestCard
                    key={vacationRequest.id || index}
                    vacationRequest={vacationRequest}
                    teamMember={teamMember}
                    onClick={() => {
                      setSelectedVacationRequest(vacationRequest)
                      setVisibleRequestModal(true)
                      setIsProgrammedPaymentsModalVisible(false)
                      setIsOverduePaymentsModalVisible(false)
                      setOriginModal(VACATION_REQUEST_PLUS_DAYS.OVERDUE)
                    }}
                  />
                )
              })
            }
          </div>
        </div>
      </ModalForm>
      <ModalFormApprovedRequestReview
        visible={visibleRequestModal}
        setVisible={handleRequestModalVisibility}
        selectedVacationRequest={selectedVacationRequest}
        triggerRefresh={handleTriggerRefresh}
        className="custom-close-icon"
      />
    </>
  )
}

export default AdminVacationsResume

AdminVacationsResume.propTypes = {
  year: PropTypes.number,
  setLoading: PropTypes.func,
}
